import React, { useEffect } from 'react';
import { detectMediaType, getYouTubeEmbedUrl, getVimeoEmbedUrl, normalizeMediaUrl, downloadMediaBlob } from '../../../utils/mediaUtils';
import { ExpandableMedia } from '../../../components/ui/MediaLightbox';

// Disable ALL OpenType ligatures. Some fonts render sequences like "(2)" as Unicode
// circled glyphs (②) via standard/contextual ligatures, so we kill them globally here.
const FORMATTED_TEXT_STYLE = {
    fontVariantLigatures: 'none',
    fontFeatureSettings: '"liga" 0, "clig" 0, "calt" 0, "dlig" 0, "hlig" 0',
};

const FormattedText = ({ text, className = "" }) => (
    <div className={className} style={FORMATTED_TEXT_STYLE} dangerouslySetInnerHTML={{ __html: text || '' }} />
);

const MediaBlock = ({ media, language, className = "" }) => {
    // Resolve URL: prefer language-specific (urlFr/urlEn), fallback to universal (url)
    const rawUrl = (language === 'fr' ? media?.urlFr : media?.urlEn) || media?.url;
    if (!rawUrl) return null;
    const resolvedUrl = normalizeMediaUrl(rawUrl);
    const hasCustomWidth = media.width && media.width !== 'auto';
    const mediaType = detectMediaType(resolvedUrl);
    const isPdf = mediaType === 'pdf';
    const figureStyle = isPdf ? { width: '100%' } : (hasCustomWidth ? { width: media.width, maxWidth: '100%' } : {});

    const renderMedia = () => {
        switch (mediaType) {
            case 'youtube':
                return <iframe src={getYouTubeEmbedUrl(resolvedUrl)} className="w-full aspect-video rounded-lg border border-gray-200 dark:border-gray-700"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="YouTube" />;
            case 'vimeo':
                return <iframe src={getVimeoEmbedUrl(resolvedUrl)} className="w-full aspect-video rounded-lg border border-gray-200 dark:border-gray-700"
                    allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title="Vimeo" />;
            case 'video':
                return <video src={resolvedUrl} controls className="w-full rounded-lg border border-gray-200 dark:border-gray-700" />;
            case 'audio':
                return <audio src={resolvedUrl} controls className="w-full" />;
            case 'pdf':
                return (
                    <div className="space-y-2">
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700"
                            style={{ resize: 'vertical', overflow: 'hidden', height: '24rem', minHeight: '150px' }}>
                            <iframe src={resolvedUrl} className="w-full h-full" title="PDF" />
                        </div>
                        <button type="button" onClick={() => downloadMediaBlob(resolvedUrl)}
                            className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline">
                            Télécharger le PDF
                        </button>
                    </div>
                );
            default: { // image or unknown
                const imgStyle = hasCustomWidth ? { width: '100%' } : { maxWidth: '100%' };
                return <img src={resolvedUrl} alt="" style={imgStyle}
                    className="rounded-lg border border-gray-200 dark:border-gray-700 object-contain" />;
            }
        }
    };

    const legendText = media.legend && media.legend[language];

    // Audio: render inline player without ExpandableMedia wrapper
    if (mediaType === 'audio') {
        return (
            <div className={`w-full max-w-xl ${className}`}>
                <div className="flex items-center gap-3 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl px-4 py-3">
                    <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
                        </svg>
                    </div>
                    <audio src={resolvedUrl} controls preload="metadata" className="flex-1 min-w-0 h-10" />
                </div>
                {legendText && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 italic text-center">{legendText}</p>
                )}
            </div>
        );
    }

    return (
        <figure className={`flex flex-col ${isPdf ? 'items-stretch' : 'items-center'} ${className}`} style={figureStyle}>
            <ExpandableMedia url={resolvedUrl} legend={legendText} className={isPdf ? 'w-full' : ''}>
                {renderMedia()}
            </ExpandableMedia>
            {legendText && (
                <figcaption className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic text-center">
                    {legendText}
                </figcaption>
            )}
        </figure>
    );
};

const ParagraphBlocks = ({ paragraphs, language }) => {
    if (!paragraphs?.length) return null;
    return (
        <div className="space-y-4">
            {paragraphs.map(p => {
                const text = p.text?.[language];
                const mediaPos = p.media?.position || 'bottom';
                const showTop = mediaPos === 'top';
                const showSide = mediaPos === 'left' || mediaPos === 'right';

                if (showSide && p.media?.url) {
                    return (
                        <div key={p.id} className={`flex gap-4 items-start ${mediaPos === 'right' ? 'flex-row' : 'flex-row-reverse'}`}>
                            <div className="flex-1 min-w-0">
                                {text && <FormattedText text={text} className="text-sm md:text-base text-gray-700 dark:text-gray-300 leading-relaxed" />}
                            </div>
                            <MediaBlock media={p.media} language={language} className="shrink-0" />
                        </div>
                    );
                }

                return (
                    <div key={p.id}>
                        {showTop && <MediaBlock media={p.media} language={language} className="mb-2" />}
                        {text && <FormattedText text={text} className="text-sm md:text-base text-gray-700 dark:text-gray-300 leading-relaxed" />}
                        {!showTop && <MediaBlock media={p.media} language={language} className="mt-2" />}
                    </div>
                );
            })}
        </div>
    );
};

// Icon mapping based on matière code/name keywords
const getMatiereIcon = (code, name) => {
    const key = `${code || ''} ${name?.fr || ''} ${name?.en || ''}`.toLowerCase();

    if (key.match(/droit|law|leg|regl/)) return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" /></svg>
    );
    if (key.match(/met[eé]o|weather|meteo/)) return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" /></svg>
    );
    if (key.match(/nav/)) return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
    );
    if (key.match(/instrum|ifr|vol aux/)) return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
    );
    if (key.match(/humain|human|facteur|factor/)) return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
    );
    if (key.match(/perf/)) return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
    );
    if (key.match(/proc[eé]d|op[eé]ra|ops/)) return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
    );
    if (key.match(/connais|general|g[eé]n[eé]r/)) return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
    );
    if (key.match(/struct|cellul|airframe/)) return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>
    );
    if (key.match(/commun|radio/)) return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" /></svg>
    );
    return (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" /></svg>
    );
};

const ExamQuestion = ({
    isReviewMode,
    isPaused,
    setIsPaused,
    calculatedScore,
    passingScore,
    timeInSeconds,
    selectedOption,
    showFeedback,
    onOptionSelect,
    onNext,
    onPrev,
    formatTime,
    currentQuestion,
    currentQuestionIndex,
    questions,
    answers,
    onDemandAnswers,
    language,
    mode,
    finishExam,
    matiereResults,
    minSubScore,
    category,
    isQuiz = false,
    sessionDate,
    isHistoryVuReview = false
}) => {

    // Safety check
    if (!questions || !questions.length || !currentQuestion) return null;

    // Helper: true if the question has a real answer (not 0/undefined/"0")
    const isRealAnswer = (qId) => {
        const v = answers[qId];
        return v !== undefined && v !== 0 && v !== '0';
    };

    // Auto-scroll to explanation when feedback is shown in flash mode
    useEffect(() => {
        if (mode === 'flash' && showFeedback && !isReviewMode) {
            const timer = setTimeout(() => {
                const explanationElement = document.getElementById('explanation-section');
                if (explanationElement) {
                    explanationElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [showFeedback, mode, isReviewMode]);

    return (
        <>
            <main data-help="session-question" className="flex-1 overflow-y-auto w-full custom-scrollbar">
                <div className={`min-h-full flex flex-col items-center justify-start lg:justify-center ${isReviewMode ? 'max-w-7xl' : 'max-w-3xl'} mx-auto w-full relative transition-all duration-500 px-6 lg:px-0`}>
                    {isPaused ? (
                        <div className="text-center animate-in fade-in zoom-in duration-300">
                            <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">{language === 'fr' ? 'Session en pause' : 'Session paused'}</h2>
                            <button
                                onClick={() => setIsPaused(false)}
                                className="px-10 py-3 bg-gray-900 dark:bg-white text-white dark:text-black rounded-2xl font-bold text-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition"
                            >
                                {language === 'fr' ? 'Reprendre' : 'Resume'}
                            </button>
                        </div>
                    ) : (
                        <div className={`w-full animate-in slide-in-from-bottom-4 duration-500 ${isReviewMode ? 'flex flex-col gap-8' : 'space-y-6'} px-3 md:px-0`}>
                            {/* Stats bar (review mode only) — always on top */}
                            {isReviewMode && (
                                <div className="flex flex-col gap-3 w-full">
                                    <div className={`grid ${isQuiz ? 'grid-cols-2 md:grid-cols-2' : 'grid-cols-2 md:grid-cols-4'} gap-2 w-full`}>
                                        {sessionDate && (
                                            <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl p-3 md:p-4 text-center">
                                                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1">
                                                    {language === 'fr' ? 'Validation le' : 'Validated on'}
                                                </p>
                                                <div className="text-base md:text-lg font-bold text-gray-900 dark:text-white">
                                                    {new Date(sessionDate).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                </div>
                                                <p className="text-xs text-gray-400 dark:text-gray-500 font-medium">
                                                    {new Date(sessionDate).toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                                                </p>
                                            </div>
                                        )}
                                        <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl p-3 md:p-4 text-center">
                                            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1">
                                                {language === 'fr' ? 'Votre score' : 'Your score'}
                                            </p>
                                            <div className={`text-2xl md:text-3xl font-black ${isQuiz ? 'text-gray-900 dark:text-white' : (calculatedScore >= passingScore ? 'text-emerald-500' : 'text-red-500')}`}>
                                                {calculatedScore}<span className="text-sm md:text-base">%</span>
                                            </div>
                                        </div>
                                        {!isQuiz && (
                                            <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl p-3 md:p-4 text-center">
                                                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1">
                                                    {language === 'fr' ? 'Passage' : 'Pass mark'}
                                                </p>
                                                <div className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white">
                                                    {passingScore}<span className="text-sm md:text-base text-gray-400">%</span>
                                                </div>
                                            </div>
                                        )}
                                        <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl p-3 md:p-4 text-center">
                                            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1">
                                                {language === 'fr' ? 'Temps' : 'Time'}
                                            </p>
                                            <div className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white">
                                                {formatTime(timeInSeconds)}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Per-matière breakdown */}
                                    {(category === 'complet' || category === 'full') && minSubScore > 0 && matiereResults && matiereResults.length > 0 && (
                                        <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-4 rounded-2xl relative overflow-hidden">
                                            <p className="text-gray-500 dark:text-gray-400 text-[10px] font-black uppercase tracking-widest mb-3">
                                                {language === 'fr' ? 'Par matière' : 'By subject'}
                                            </p>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1.5">
                                                {matiereResults.map((m) => (
                                                    <div key={m.id} className="flex items-center gap-2">
                                                        <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                                                            m.passed ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400' : 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400'
                                                        }`}>
                                                            {getMatiereIcon(m.code, m.name)}
                                                        </div>
                                                        <span className="flex-1 text-xs text-gray-700 dark:text-gray-300 truncate">
                                                            {language === 'fr' ? m.name.fr : m.name.en}
                                                        </span>
                                                        <span className={`text-xs font-bold tabular-nums ${
                                                            m.passed ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                                                        }`}>
                                                            {m.score}%
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                            {minSubScore > 0 && (
                                                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2.5 text-center">
                                                    Min. {minSubScore}% {language === 'fr' ? 'par matière' : 'per subject'}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="space-y-8 w-full min-w-0">
                                <div className="space-y-3">
                                    <span className="text-gray-500 dark:text-gray-500 font-bold uppercase tracking-widest text-xs">
                                        {language === 'fr' ? `Question ${currentQuestionIndex + 1}` : `Question ${currentQuestionIndex + 1}`}
                                    </span>
                                    <h2 className={`${isReviewMode ? 'text-base md:text-2xl' : 'text-lg md:text-3xl'} font-bold leading-tight text-gray-900 dark:text-white`}>
                                        <FormattedText text={currentQuestion.text[language]} />
                                    </h2>
                                    {currentQuestion.paragraphs?.length > 0 && (
                                        <ParagraphBlocks paragraphs={currentQuestion.paragraphs} language={language} />
                                    )}
                                </div>

                                {/* Special states for history review */}
                                {(() => {
                                    const isNewVuQuestion = isHistoryVuReview && isReviewMode && answers[currentQuestion.id] === undefined;
                                    const userAns = answers[currentQuestion.id];
                                    const isStaleAnswer = isReviewMode && userAns !== undefined && userAns !== 0 && userAns !== '0'
                                        && currentQuestion.options?.length > 0
                                        && !currentQuestion.options.some(opt => String(opt.id) === String(userAns));

                                    if (isNewVuQuestion) {
                                        return (
                                            <div className="flex items-center gap-4 p-5 md:p-6 bg-amber-50 dark:bg-amber-500/10 border-2 border-amber-200 dark:border-amber-500/20 rounded-2xl">
                                                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                                                    <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                                    </svg>
                                                </div>
                                                <p className="text-sm md:text-base font-medium text-amber-800 dark:text-amber-300">
                                                    {language === 'fr'
                                                        ? 'Nouvelle question ajoutée depuis votre session. Testez vos connaissances avec une nouvelle session !'
                                                        : 'New question added since your session. Test your knowledge with a new session!'}
                                                </p>
                                            </div>
                                        );
                                    }
                                    if (isStaleAnswer) {
                                        return (
                                            <div className="flex items-center gap-4 p-5 md:p-6 bg-gray-100 dark:bg-white/5 border-2 border-gray-200 dark:border-white/10 rounded-2xl">
                                                <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-white/10 flex items-center justify-center flex-shrink-0">
                                                    <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                    </svg>
                                                </div>
                                                <p className="text-sm md:text-base font-medium text-gray-600 dark:text-gray-300">
                                                    {language === 'fr'
                                                        ? 'Cette question a été modifiée depuis votre session. Votre réponse d\'origine n\'est plus comparable.'
                                                        : 'This question was modified since your session. Your original answer is no longer comparable.'}
                                                </p>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div className="grid grid-cols-1 gap-3">
                                            {[...currentQuestion.options].sort((a, b) => {
                                                // Push "Toutes ces propositions" / "Aucune de ces propositions" to end
                                                const rank = (textObj) => {
                                                    const raw = (textObj?.fr || textObj?.en || '').replace(/<[^>]*>/g, '').toLowerCase();
                                                    if (/\btoutes?\b.*\b(propositions?|r[eé]ponses?)\b/.test(raw) || /\ball\s+(of\s+)?the\s+above\b/.test(raw)) return 1;
                                                    if (/\baucune?\b.*\b(propositions?|r[eé]ponses?)\b/.test(raw) || /\bnone\s+(of\s+)?the\s+above\b/.test(raw)) return 1;
                                                    return 0;
                                                };
                                                return rank(a.text) - rank(b.text);
                                            }).map((option, idx) => {
                                                const secret = onDemandAnswers[currentQuestion.id];
                                                const correctId = secret?.correctReponseId;

                                                const isSelected = selectedOption != null && String(selectedOption) === String(option.id);

                                                // In review mode or flash mode with feedback, show only selected and correct answer
                                                const shouldFilter = isReviewMode || (mode === 'flash' && showFeedback);
                                                if (shouldFilter && !isSelected && (correctId == null || String(option.id) !== String(correctId))) return null;
                                                const isCorrect = correctId != null && String(option.id) === String(correctId);
                                                const shouldShowCorrectness = (mode === 'flash' && showFeedback) || isReviewMode;

                                                let variantClasses = 'bg-white dark:bg-white/5 border-2 border-gray-200 dark:border-white/15 hover:bg-gray-50 dark:hover:bg-white/10 shadow-sm dark:shadow-none';

                                                if (isSelected) {
                                                    if (shouldShowCorrectness) {
                                                        variantClasses = isCorrect
                                                            ? 'bg-emerald-100 border-emerald-500 dark:bg-emerald-500/20 dark:border-emerald-500 opacity-100'
                                                            : 'bg-red-100 border-red-500 dark:bg-red-500/20 dark:border-red-500 opacity-100';
                                                    } else {
                                                        variantClasses = 'bg-blue-50 border-blue-500 dark:bg-white/20 dark:border-white shadow-md';
                                                    }
                                                } else if (shouldShowCorrectness && isCorrect) {
                                                    variantClasses = 'bg-emerald-50 border-emerald-500/50 dark:bg-emerald-500/20 dark:border-emerald-500';
                                                }

                                                return (
                                                    <button
                                                        key={option.id || idx}
                                                        onClick={() => onOptionSelect(option.id)}
                                                        disabled={(mode === 'flash' && showFeedback) || isReviewMode}
                                                        className={`w-full ${isReviewMode ? 'p-3' : 'p-4 md:p-5'} text-left rounded-2xl transition-all flex items-center justify-between group ${variantClasses}`}
                                                    >
                                                        <FormattedText className={`${isReviewMode ? 'text-sm md:text-base' : 'text-lg md:text-2xl'} font-medium flex-1 ${isSelected || (shouldShowCorrectness && isCorrect) ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`} text={option.text[language]} />
                                                        {shouldShowCorrectness && isCorrect && (
                                                            <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}

                                {/* Explanation — hide for new vu-examen questions */}
                                {(showFeedback || isReviewMode) && onDemandAnswers[currentQuestion.id] && !(isHistoryVuReview && answers[currentQuestion.id] === undefined) && (
                                    <div id="explanation-section" className="p-4 md:p-5 bg-gray-100 dark:bg-white/5 rounded-2xl border border-gray-200 dark:border-white/10 animate-in fade-in slide-in-from-top-2 duration-300">
                                        <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start">
                                            <img
                                                src="/img/application/explanationLogo.png"
                                                alt=""
                                                className="hidden md:block w-12 h-12 shrink-0"
                                            />
                                            <div className="space-y-2 md:space-y-3 flex-1">
                                                <div>
                                                    <h4 className="font-bold text-sm md:text-lg text-gray-900 dark:text-white">{language === 'fr' ? "Explication de l'instructeur" : "Instructor Explanation"}</h4>
                                                    <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">Team nizus</p>
                                                </div>
                                                <div className="w-full h-px bg-gray-200 dark:bg-white/10"></div>
                                                <FormattedText
                                                    className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm md:text-base"
                                                    text={onDemandAnswers[currentQuestion.id]?.explanation[language]}
                                                />
                                                {onDemandAnswers[currentQuestion.id]?.explicationParagraphs?.length > 0 && (
                                                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-white/10">
                                                        <ParagraphBlocks
                                                            paragraphs={onDemandAnswers[currentQuestion.id].explicationParagraphs}
                                                            language={language}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Flash Mode Next/Finish Button */}
                                        {mode === 'flash' && !isReviewMode && (
                                            <button
                                                onClick={() => {
                                                    if (currentQuestionIndex < questions.length - 1) {
                                                        onNext();
                                                    } else {
                                                        finishExam();
                                                    }
                                                }}
                                                className="mt-4 px-6 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-black rounded-xl font-bold hover:bg-gray-800 dark:hover:bg-gray-200 transition flex items-center gap-2 text-sm"
                                            >
                                                {currentQuestionIndex < questions.length - 1
                                                    ? (language === 'fr' ? 'Question suivante' : 'Next Question')
                                                    : (language === 'fr' ? 'Voir les résultats' : 'View Results')
                                                }
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </main>

            {/* Floating Navigation Arrows (Centered) - Always Visible */}
            <div className="absolute top-1/2 left-0 right-0 transform -translate-y-1/2 z-40 pointer-events-none flex justify-between px-1.5 lg:px-8">
                <button
                    onClick={onPrev}
                    disabled={currentQuestionIndex === 0 || (mode === 'flash' && !isReviewMode && !isRealAnswer(questions[currentQuestionIndex - 1]?.id))}
                    className="w-[30px] h-10 lg:w-14 lg:h-14 rounded-full lg:rounded-full rounded-[20px] bg-white dark:bg-gray-800 pointer-events-auto flex items-center justify-center text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-0 disabled:cursor-auto shadow-xl border border-gray-200 dark:border-gray-700"
                    aria-label="Previous Question"
                >
                    <svg className="w-4 h-4 lg:w-8 lg:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>

                <button
                    onClick={onNext}
                    disabled={(currentQuestionIndex === questions.length - 1 && !(mode === 'classic' && !isReviewMode)) || (mode === 'flash' && !isReviewMode && !isRealAnswer(questions[currentQuestionIndex]?.id))}
                    className="w-[30px] h-10 lg:w-14 lg:h-14 rounded-full lg:rounded-full rounded-[20px] bg-blue-600 dark:bg-blue-500 pointer-events-auto flex items-center justify-center text-white hover:bg-blue-700 dark:hover:bg-blue-600 transition disabled:opacity-0 disabled:cursor-auto shadow-xl shadow-blue-500/30 dark:shadow-blue-900/50"
                    aria-label="Next Question"
                >
                    <svg className="w-4 h-4 lg:w-8 lg:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
        </>
    );
};

export default ExamQuestion;
